/**
 * The graph canvas: three.js on an orthographic (flat 2D) camera.
 *
 * Built to stay light as the graph grows:
 *   - ONE draw call for every node (THREE.Points: a flat disc, no glow) and ONE
 *     for every link (THREE.LineSegments: each link is a gentle S-curve drawn
 *     as a few segments, dotted in the shader).
 *   - Layout runs in a Web Worker (graphLayout.worker.js); positions arrive as
 *     transferred Float32Arrays and are copied straight into GPU buffers.
 *   - Frames are drawn ON DEMAND: only while the layout moves, the camera
 *     moves, an emphasis fade runs, or hovered links are marching. A settled,
 *     untouched graph draws nothing and the worker has stopped — ~0% CPU.
 *   - Labels are a small pool of DOM elements (never one per node).
 *   - Positions are remembered (localStorage), so reopening shows the same map.
 *
 * Owner direction (13.09.2026): no shadow around the bubbles; hover must not
 * darken the scene — unrelated items fade (as search does) and the hovered
 * item's links become animated dotted lines; links curve like a snake instead
 * of running straight; right-click a node for a menu; "Link to…" drags a live
 * connection line to the item you click.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import createLayoutWorker from './createLayoutWorker';
import { isDarkTheme } from './graphTheme';
import { loadPositions } from './graphCanvasConfig';
import { EDGE_FRAG, EDGE_VERT, NODE_FRAG, NODE_VERT } from './graphShaders';
import { graphInteraction } from './graphInteraction';
import { graphScene } from './graphScene';

 // how visible unrelated items stay while something is in focus
const GraphCanvas = forwardRef(function GraphCanvas(
  { graph, knownIds, selectedId, highlightIds, onSelect, onOpen, onHover, onContextMenu, onLinkDrop, onLinkModeChange },
  ref
) {
  const hostRef = useRef(null);
  const labelsRef = useRef(null);
  const api = useRef({});
  const props = useRef({});
  props.current = { onSelect, onOpen, onHover, knownIds, onContextMenu, onLinkDrop, onLinkModeChange };

  // ── one-time three.js + worker setup ─────────────────────────────────────
  useEffect(() => {
    // Values this effect reassigns, in one object so its parts can live in their own modules.
    const live = {};
    const host = hostRef.current;
    const labelHost = labelsRef.current;
    const reduceMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'default' });
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(pixelRatio);
    renderer.setClearColor(0x000000, 0);
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -10, 10);
    camera.position.set(0, 0, 5);

    const nodeMaterial = new THREE.ShaderMaterial({
      vertexShader: NODE_VERT,
      fragmentShader: NODE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uZoom: { value: 1 },
        uPixelRatio: { value: pixelRatio },
        uRingColor: { value: new THREE.Color('#8869eb') },
      },
    });
    // fwidth() needs derivatives on WebGL1 contexts.
    nodeMaterial.extensions = { derivatives: true };
    const edgeUniforms = {
      uAccent: { value: new THREE.Color('#8869eb') },
      uZoom: { value: 1 },
      uTime: { value: 0 },
    };
    const edgeMaterial = new THREE.ShaderMaterial({
      vertexShader: EDGE_VERT,
      fragmentShader: EDGE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: edgeUniforms,
    });
    // The live "link to…" line: its own tiny geometry, same dotted shader.
    const LINK_SEG = 24;
    const linkGeom = new THREE.BufferGeometry();
    linkGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(LINK_SEG * 2 * 3), 3));
    linkGeom.setAttribute('aColor', new THREE.BufferAttribute(new Float32Array(LINK_SEG * 2 * 3), 3));
    linkGeom.setAttribute('aAlpha', new THREE.BufferAttribute(new Float32Array(LINK_SEG * 2).fill(1), 1));
    linkGeom.setAttribute('aMarch', new THREE.BufferAttribute(new Float32Array(LINK_SEG * 2).fill(1), 1));
    linkGeom.setAttribute('aDist', new THREE.BufferAttribute(new Float32Array(LINK_SEG * 2), 1));
    const linkLine = new THREE.LineSegments(linkGeom, edgeMaterial);
    linkLine.frustumCulled = false;
    linkLine.renderOrder = 2;
    linkLine.visible = false;
    scene.add(linkLine);

    live.points = null;
    live.lines = null;

    // Graph state (plain arrays — nothing React-reactive in the frame loop).
    const s = {
      nodes: [],
      edges: [],
      index: new Map(),
      degree: [],
      neighbours: [],
      edgeEnds: new Uint32Array(0),
      bend: new Float32Array(0),
      seg: 12,
      radius: new Float32Array(0),
      alpha: new Float32Array(0),
      alphaTarget: new Float32Array(0),
      edgeAlpha: new Float32Array(0),
      edgeAlphaTarget: new Float32Array(0),
      march: new Float32Array(0),
      labelOrder: [],
      hovered: -1,
      // Hover coming from outside the canvas (the details panel's rows): same
      // emphasis as pointer hover, but it never touches the cursor.
      externalHover: -1,
      selected: -1,
      highlight: null,
      width: 1,
      height: 1,
      zoom: 1,
      dark: isDarkTheme(),
      saved: loadPositions(),
      settled: false,
      hasPositions: false,
      // A fresh layout starts packed and spreads out; fit again once it settles,
      // unless the user already moved the camera.
      fitOnSettle: false,
      userMoved: false,
      // Link mode: index of the source node, and the cursor in world units.
      linkFrom: -1,
      linkCursor: { x: 0, y: 0 },
      marching: false,
      startedAt: performance.now(),
    };

    const selectedIdRef = { current: null };
    const worker = createLayoutWorker();
    live.frame = 0;
    live.saveTimer = null;
    // Every 'set' starts a new generation; ticks and drags from an older one are
    // dropped (a same-sized graph would otherwise take another graph's positions).
    live.generation = 0;
    live.drag = null;

  const {
    requestRender,
    resize,
    applyCamera,
    toWorld,
    posAttr,
    pick,
    applyColors,
    applyEmphasis,
    pool,
    setGraph,
  } = graphScene({
    s,
    camera,
    worker,
    scene,
    live,
    host,
    props,
    labelHost,
    renderer,
    nodeMaterial,
    edgeUniforms,
    edgeMaterial,
    LINK_SEG,
    linkGeom,
    selectedIdRef,
    cancelLink: (...args) => cancelLink(...args),
    fitView: (...args) => fitView(...args),
  });

  const {
    animateCamera,
    fitView,
    focusNode,
    startLink,
    cancelLink,
    canvas,
    onWheel,
    onDown,
    onMove,
    onUp,
    onDouble,
    onContext,
    onLeave,
    onKey,
    onCancel,
  } = graphInteraction({
    s,
    camera,
    worker,
    live,
    props,
    reduceMotion,
    renderer,
    linkLine,
    requestRender,
    applyCamera,
    toWorld,
    posAttr,
    pick,
    applyEmphasis,
  });

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(host);
    const themeObserver = new MutationObserver(applyColors);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
    resize();

    api.current = {
      setGraph,
      fitView,
      focusNode,
      startLink,
      cancelLink,
      setExternalHover: (id) => {
        const i = id != null ? s.index.get(id) ?? -1 : -1;
        if (i === s.externalHover) return;
        s.externalHover = i;
        applyEmphasis();
      },
      setSelected: (id) => {
        selectedIdRef.current = id;
        s.selected = id != null ? s.index.get(id) ?? -1 : -1;
        applyEmphasis();
      },
      setHighlight: (ids) => {
        s.highlight = ids && ids.size ? ids : null;
        applyEmphasis();
      },
      zoomBy: (factor) => animateCamera(camera.position.x, camera.position.y, s.zoom * factor),
    };

    return () => {
      cancelAnimationFrame(live.frame);
      cancelAnimationFrame(live.camAnim);
      clearTimeout(live.saveTimer);
      worker.postMessage({ type: 'stop' });
      worker.terminate();
      resizeObserver?.disconnect();
      themeObserver.disconnect();
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('contextmenu', onContext);
      window.removeEventListener('keydown', onKey, true);
      canvas.removeEventListener('pointercancel', onCancel);
      canvas.removeEventListener('lostpointercapture', onCancel);
      canvas.removeEventListener('dblclick', onDouble);
      live.points?.geometry.dispose();
      live.lines?.geometry.dispose();
      linkGeom.dispose();
      nodeMaterial.dispose();
      edgeMaterial.dispose();
      // Release the GL context now; repeated page switches otherwise pile up
      // contexts until the browser drops the oldest.
      renderer.forceContextLoss();
      renderer.dispose();
      canvas.remove();
      pool.forEach((el) => el.remove());
    };
  }, []);

  useEffect(() => {
    api.current.setGraph?.(graph);
  }, [graph]);

  useEffect(() => {
    api.current.setSelected?.(selectedId ?? null);
  }, [selectedId, graph]);

  useEffect(() => {
    api.current.setHighlight?.(highlightIds || null);
  }, [highlightIds, graph]);

  useImperativeHandle(ref, () => ({
    fitView: (ids) => api.current.fitView?.(true, ids || null),
    focusNode: (id) => api.current.focusNode?.(id),
    zoomBy: (f) => api.current.zoomBy?.(f),
    startLink: (id, clientPoint) => api.current.startLink?.(id, clientPoint),
    cancelLink: () => api.current.cancelLink?.(),
    hoverNode: (id) => api.current.setExternalHover?.(id ?? null),
  }));

  return (
    <div className="graph-canvas" ref={hostRef}>
      <div className="graph-labels" ref={labelsRef} aria-hidden="true" />
    </div>
  );
});

export default GraphCanvas;
